import React, { useState, useEffect, useRef } from "react";
import { auth } from "../../config/firebase";
import { onAuthStateChanged, signInWithCustomToken } from "firebase/auth";
import ChatService from "../../services/FirebaseChatService";
import MainButton from "../../components/Elements/MainButton/MainButton";
import MainInput from "../../components/Elements/MainInput/MainInput";
import PageHeader from "../../components/Elements/PageHeader/PageHeader";
import "./SupportChats.css";

const parseDate = (val) => {
  if (!val) return null;
  if (typeof val.toDate === "function") {
    try {
      return val.toDate();
    } catch {
      return null;
    }
  }
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  if (typeof val === "number") return new Date(val);
  if (typeof val === "string") {
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }
  if (val.seconds) return new Date(val.seconds * 1000);
  return null;
};

const SupportChats = () => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [authError, setAuthError] = useState(null);

  const messagesEndRef = useRef(null);

  // Auto-scroll na dół listy wiadomości
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [messages, selectedChat?.id]);

  // 1. Zjednoczona autoryzacja Firebase z weryfikacją i odświeżaniem uprawnień
  useEffect(() => {
    const loginWithBackendToken = async () => {
      const token = localStorage.getItem("firebaseToken");
      try {
        if (!auth.currentUser) {
          if (token) {
            const cred = await signInWithCustomToken(auth, token);
            await cred.user.getIdTokenResult(true);
            setAuthError(null);
          }
        } else {
          // If already signed in, check claims and force refresh
          try {
            const tokenResult = await auth.currentUser.getIdTokenResult(true);
            if (!tokenResult.claims.admin && !tokenResult.claims.support && token) {
              console.log("Firebase user missing admin/support claims, re-authenticating with custom token...");
              await auth.signOut();
              const cred = await signInWithCustomToken(auth, token);
              await cred.user.getIdTokenResult(true);
            }
            setAuthError(null);
          } catch (refreshErr) {
            console.warn("Failed refreshing claims:", refreshErr);
            if (token) {
              const cred = await signInWithCustomToken(auth, token);
              await cred.user.getIdTokenResult(true);
            }
          }
        }
      } catch (error) {
        console.error("Firebase custom token login failed:", error);
        setAuthError(
          error.code === "auth/invalid-custom-token"
            ? "Your chat session has expired. Please log out and sign in again to refresh support chat credentials."
            : error.message,
        );
      }
    };

    loginWithBackendToken();

    const unsub = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);
    });

    return unsub;
  }, []);

  // 2. Subskrypcja wszystkich czatów
  useEffect(() => {
    if (!firebaseUser) {
      setChats([]);
      return;
    }
    const unsub = ChatService.subscribeToAllChats(
      setChats,
      (err) => {
        console.error("Error subscribing to chats list:", err);
      },
    );
    return unsub;
  }, [firebaseUser]);

  // 3. Subskrypcja wiadomości wybranego czatu z czyszczeniem poprzedniego stanu
  useEffect(() => {
    if (!selectedChat?.id || !firebaseUser) {
      setMessages([]);
      setMessagesLoading(false);
      return;
    }

    setMessages([]);
    setMessagesLoading(true);
    setChatError(null);

    if (ChatService.markAsReadByAdmin) {
      ChatService.markAsReadByAdmin(selectedChat.id);
    }

    const unsub = ChatService.subscribeToChatMessages(
      selectedChat.id,
      (newMessages) => {
        setMessages(newMessages);
        setMessagesLoading(false);
        setChatError(null);
      },
      (err) => {
        console.error("Error loading chat messages:", err);
        setMessagesLoading(false);
        if (err.code === "permission-denied") {
          setChatError(
            "Permission denied: You do not have permission to view this chat's messages. Please verify that your account has admin/support role or verify Firebase Security Rules.",
          );
        } else {
          setChatError(err.message || "Failed to load messages.");
        }
      },
    );
    return unsub;
  }, [selectedChat?.id, firebaseUser]);

  // Auto-selekcja pierwszego czatu
  useEffect(() => {
    if (chats.length > 0 && !selectedChat) {
      setSelectedChat(chats[0]);
    }
  }, [chats, selectedChat]);

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedChat?.id || !firebaseUser) return;

    try {
      await ChatService.sendMessage(selectedChat.id, replyText.trim(), firebaseUser.email, "support");
      setReplyText("");
    } catch (err) {
      console.error("Error sending reply:", err);
      alert(`Failed to send message: ${err.message || "Please check permissions."}`);
    }
  };

  const groupMessagesByDate = (messagesList) => {
    if (!messagesList || !messagesList.length) return [];

    const groups = [];
    const firstDate = parseDate(messagesList[0].createdAt) || new Date();
    let currentGroup = { date: firstDate, messages: [messagesList[0]], isNewDay: true };

    for (let i = 1; i < messagesList.length; i++) {
      const msgDate = parseDate(messagesList[i].createdAt) || new Date();
      const prevDate = currentGroup.date;

      const isNew = !isSameDay(msgDate, prevDate);

      if (isNew) {
        groups.push(currentGroup);
        currentGroup = { date: msgDate, messages: [messagesList[i]], isNewDay: true };
      } else {
        currentGroup.messages.push(messagesList[i]);
      }
    }
    groups.push(currentGroup);
    return groups;
  };

  const formatChatListDate = (timestamp) => {
    const date = parseDate(timestamp);
    if (!date) return "Never";

    const now = new Date();
    if (isSameDay(date, now)) return "Today";

    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);
    if (isSameDay(date, yesterday)) return "Yesterday";

    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const formatDate = (date) => {
    if (!date) return "Today";
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (isSameDay(date, today)) return "Today";
    if (isSameDay(date, yesterday)) return "Yesterday";

    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatMessageTime = (createdAt) => {
    const date = parseDate(createdAt);
    if (!date) return "";
    return date.toLocaleTimeString("pl-PL", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  return (
    <main>
      <PageHeader
        title="Support Chats"
        description="View chat and message back users, to help them with their issues."
      />
      <div className="admin-help-chats">
        <div className="unified-chat-container">
          <div className="chats-split-layout">
            {/* LEFT: Chats List */}
            <div className="chats-list-panel">
              <div className="panel-header">
                <h2>Chats</h2>
              </div>
              <div className="chats-list">
                {chats.map((chat) => (
                  <div
                    key={chat.id}
                    className={`chat-item ${selectedChat?.id === chat.id ? "active" : ""}`}
                    onClick={() => setSelectedChat(chat)}
                  >
                    <div className="chat-user">{chat.email || chat.userId || "Unknown"}</div>
                    <div className="chat-preview">{chat.lastMessage}</div>{" "}
                    <div className="chat-time">{formatChatListDate(chat.lastMessageAt)}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="vertical-divider" />

            {/* RIGHT: Chat Detail */}
            <div className="chat-detail-panel">
              {selectedChat ? (
                <>
                  <div className="panel-header">
                    <h3>Chat with {selectedChat.email || selectedChat.userId || "User"}</h3>
                  </div>

                  {authError && (
                    <div
                      style={{
                        background: "#fff3cd",
                        color: "#856404",
                        padding: "10px 16px",
                        fontSize: "13px",
                        borderBottom: "1px solid #ffeeba",
                      }}
                    >
                      ⚠️ {authError}
                    </div>
                  )}

                  <div className="messages-container">
                    {chatError && (
                      <div
                        style={{
                          background: "#f8d7da",
                          color: "#721c24",
                          padding: "12px 16px",
                          borderRadius: "8px",
                          fontSize: "13px",
                          lineHeight: "1.5",
                          border: "1px solid #f5c6cb",
                          margin: "12px 0",
                        }}
                      >
                        <strong>Chat Error:</strong> {chatError}
                      </div>
                    )}

                    {messagesLoading && messages.length === 0 && (
                      <div style={{ textAlign: "center", padding: "24px", color: "var(--color-text-light)" }}>
                        Loading messages...
                      </div>
                    )}

                    {!messagesLoading && !chatError && messages.length === 0 && (
                      <div style={{ textAlign: "center", padding: "24px", color: "var(--color-text-light)" }}>
                        No messages in this chat yet.
                      </div>
                    )}

                    {groupMessagesByDate(messages).map((group, idx) => (
                      <div key={group.date?.toISOString() || `group-${idx}`} className="message-group">
                        {group.isNewDay && <div className="date-divider">{formatDate(group.date)}</div>}
                        {group.messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`message ${msg.sender === "support" ? "admin-message" : "user-message"}`}
                          >
                            <div className="message-bubble">
                              <div className="message-text">{msg.text}</div>
                              <div className="message-time">{formatMessageTime(msg.createdAt)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="reply-section">
                    <MainInput
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply..."
                      className="reply-input"
                    />
                    <MainButton onClick={handleSendReply} disabled={!replyText.trim()} className="send-button">
                      Send Reply
                    </MainButton>
                  </div>
                </>
              ) : (
                <div className="no-chat-selected">
                  {chats.length === 0 ? <h3>Loading chats...</h3> : <h3>Select a conversation</h3>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SupportChats;
